import { RefObject, useLayoutEffect, useRef, useState } from 'react';
import { throttle } from 'lodash';

type Params = {
  fetchMore: () => Promise<any>;
  scrollEndOffset?: number;
  fetchDisabled: boolean;
  useWindow?: boolean;
  useChatbotEditorPageContentContainer?: boolean;
};

const getCurrentScrollPosition = <T extends HTMLElement>(
  currentRef: T | null,
  useWindow?: boolean,
) => {
  if (useWindow) {
    return window.innerHeight + document.documentElement.scrollTop;
  }

  return currentRef ? currentRef.offsetHeight + currentRef.scrollTop : 0;
};

const getFetchAtPosition = <T extends HTMLElement>(
  currentRef: T | null,
  scrollEndOffset: number,
  useWindow?: boolean,
) => {
  if (useWindow) {
    return document.documentElement.offsetHeight - scrollEndOffset;
  }

  return currentRef ? currentRef.scrollHeight - scrollEndOffset : 0;
};

export const useInfiniteScroll = <T extends HTMLElement>(
  params: Params,
): { scrollContainerRef: RefObject<T>; isFetching: boolean } => {
  const {
    fetchMore,
    fetchDisabled,
    useWindow,
    useChatbotEditorPageContentContainer,
  } = params;
  const scrollContainerRef = useRef<T>(null);
  const scrollEndOffset = params.scrollEndOffset || 50;
  const [isFetching, setIsFetching] = useState(false);

  useLayoutEffect(() => {
    // Copy ref to comply with eslint-plugin-react-hooks/exhaustive-deps
    const currentRef = useChatbotEditorPageContentContainer
      ? document.querySelector<HTMLDivElement>(
          '#chatbot-editor-page__content-container',
        )
      : scrollContainerRef.current;

    const onScroll = throttle(async () => {
      if (!isFetching && (currentRef || useWindow)) {
        const currentScrollPosition = getCurrentScrollPosition(
          currentRef,
          useWindow,
        );
        const fetchAtPosition = getFetchAtPosition(
          currentRef,
          scrollEndOffset,
          useWindow,
        );
        const reachedScrollEnd = currentScrollPosition >= fetchAtPosition;

        if (reachedScrollEnd && !fetchDisabled) {
          setIsFetching(true);
          await fetchMore();
          setIsFetching(false);
        }
      }
    }, 200);

    if (useWindow) {
      window.addEventListener('scroll', onScroll);
    } else if (currentRef) {
      currentRef.addEventListener('scroll', onScroll);
    }

    return () => {
      if (useWindow) {
        window.removeEventListener('scroll', onScroll);
      } else if (currentRef) {
        currentRef.removeEventListener('scroll', onScroll);
      }
    };
  }, [
    scrollContainerRef,
    fetchMore,
    fetchDisabled,
    isFetching,
    scrollEndOffset,
    useWindow,
    useChatbotEditorPageContentContainer,
  ]);

  return { scrollContainerRef, isFetching };
};
